import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Breadcrumbs,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";

import Header from "../../components/Header";
import { tokens } from "../../theme";
import baseRequest from "../../core/baseRequest";
import { errorHandler } from "../../core/errorHandler";
import useSignOut from "../../hooks/useSignOut";

//Files the browser can show by itself are opened in a new tab, the rest are downloaded
const VIEWABLE_TYPES = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  txt: "text/plain",
};

const getExtension = (name) =>
  name.includes(".") ? name.split(".").pop().toLowerCase() : "";

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const LibraryPage = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [controller] = useSignOut();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPath = searchParams.get("path") || "";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState();

  const showError = (error) => {
    const { status } = errorHandler(error);
    switch (status) {
      case 401:
        controller.forceLogin();
        break;
      case 403:
        navigate("/noaccess");
        break;
      case 404:
        enqueueSnackbar("This folder or file doesn't exist anymore!", {
          variant: "error",
        });
        break;
      case 503:
        enqueueSnackbar("The library folder is not reachable on the server!", {
          variant: "error",
        });
        break;
      default:
        enqueueSnackbar("Something went wrong while loading the library!", {
          variant: "error",
        });
        break;
    }
  };

  const loadFolder = async () => {
    setLoading(true);
    try {
      const res = await baseRequest.get("/library/list", {
        params: { path: currentPath },
      });
      if (res) {
        setEntries(res.data.records.entries);
      }
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = props.title || "";
  }, [props.title]);

  useEffect(() => {
    loadFolder();
  }, [currentPath]);

  useEffect(() => {
    const query = searchText.trim();
    if (query.length < 2) {
      setSearchResults();
      return;
    }

    //Wait until the user stops typing before searching the whole library
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await baseRequest.get("/library/search", {
          params: { q: query },
        });
        if (res) {
          setSearchResults(res.data.records);
        }
      } catch (error) {
        showError(error);
      }
      setLoading(false);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const openFolder = (folderPath) => {
    setSearchText("");
    setSearchParams(folderPath ? { path: folderPath } : {});
  };

  const openFile = async (entry) => {
    const viewType = VIEWABLE_TYPES[getExtension(entry.name)];
    //Open the tab before the request so the browser doesn't block it as a popup
    const newTab = viewType ? window.open("", "_blank") : null;

    try {
      const res = await baseRequest.get("/library/file", {
        params: { path: entry.path },
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: viewType || res.data.type }),
      );

      if (newTab) {
        newTab.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = entry.name;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (error) {
      newTab?.close();
      showError(error);
    }
  };

  const getIcon = (entry) => {
    if (entry.type === "folder") {
      return <FolderIcon sx={{ color: colors.orangeAccent[400] }} />;
    }
    switch (getExtension(entry.name)) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ color: colors.yoggieRed[400] }} />;
      case "doc":
      case "docx":
        return <DescriptionIcon sx={{ color: colors.blueAccent[400] }} />;
      case "xls":
      case "xlsx":
      case "xlsm":
      case "csv":
        return <TableChartIcon sx={{ color: colors.ciboInnerGreen[400] }} />;
      case "ppt":
      case "pptx":
        return <SlideshowIcon sx={{ color: colors.orangeAccent[500] }} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <ImageIcon sx={{ color: colors.omegaDeluxPurple[400] }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: colors.grey[300] }} />;
    }
  };

  const isSearching = Boolean(searchResults);
  const rows = (isSearching ? searchResults.entries : entries).map(
    (entry) => ({ ...entry, id: entry.path }),
  );

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {getIcon(row)}
          <Box>
            <Typography
              fontWeight={row.type === "folder" ? "bold" : "normal"}
              color={colors.grey[100]}
            >
              {row.name}
            </Typography>
            {isSearching ? (
              <Typography variant="body2" color={colors.grey[300]}>
                {row.path}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      ),
    },
    {
      field: "modified",
      headerName: "Modified",
      flex: 0.3,
      valueFormatter: ({ value }) =>
        value
          ? new Date(value).toLocaleString("en-us", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
            })
          : "",
    },
    {
      field: "size",
      headerName: "Size",
      flex: 0.15,
      valueFormatter: ({ value }) => formatSize(value),
    },
  ];

  const pathParts = currentPath ? currentPath.split("/") : [];

  return (
    <Box m="0 20px">
      <Header
        title="LIBRARY"
        subtitle="Engineering documents"
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Breadcrumbs maxItems={6} sx={{ color: colors.grey[300] }}>
          <Link
            component="button"
            underline="hover"
            onClick={() => openFolder("")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              color: pathParts.length
                ? colors.grey[100]
                : colors.ciboInnerGreen[400],
              fontSize: "15px",
            }}
          >
            <LocalLibraryIcon fontSize="small" />
            Library
          </Link>
          {pathParts.map((part, index) => (
            <Link
              key={index}
              component="button"
              underline="hover"
              onClick={() => openFolder(pathParts.slice(0, index + 1).join("/"))}
              sx={{
                color:
                  index === pathParts.length - 1
                    ? colors.ciboInnerGreen[400]
                    : colors.grey[100],
                fontSize: "15px",
              }}
            >
              {part}
            </Link>
          ))}
        </Breadcrumbs>
        <TextField
          variant="standard"
          placeholder="Search the whole library..."
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ width: { xs: "100%", md: "350px" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      {isSearching && searchResults.limited ? (
        <Typography color={colors.orangeAccent[400]} mt="10px">
          Showing the first {searchResults.entries.length} results. Type more to
          narrow the search.
        </Typography>
      ) : null}
      <Box
        m="20px 0 0 0"
        height="72vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.ciboInnerGreen[500],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.ciboInnerGreen[500],
          },
          "& .MuiDataGrid-cell:focus": {
            outlineColor: colors.primary[400],
          },
          "& .MuiDataGrid-cell:focus-within": {
            outlineColor: colors.primary[400],
          },
        }}
      >
        <DataGrid
          disableRowSelectionOnClick={true}
          loading={loading}
          rows={rows}
          columns={columns}
          getRowHeight={() => (isSearching ? 60 : 48)}
          onRowClick={({ row }) => {
            if (row.type === "folder") {
              openFolder(row.path);
            } else {
              openFile(row);
            }
          }}
          localeText={{
            noRowsLabel: isSearching ? "No matching files" : "This folder is empty",
          }}
        />
      </Box>
    </Box>
  );
};

export default LibraryPage;
